import React, { useState, useRef, useEffect } from "react";
import { Card, Slider, Upload, Progress, Space, Radio, Switch } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from '../components/StandardButton';

const { Dragger } = Upload;

const ImageResizer = () => {
  const { t } = useTranslation();
  const [imageFile, setImageFile] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState("");
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [originalSize, setOriginalSize] = useState(0);
  const [mode, setMode] = useState("resize");
  
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  
  const [quality, setQuality] = useState(80);
  const [targetFormat, setTargetFormat] = useState("original");
  
  const [processedImageUrl, setProcessedImageUrl] = useState("");
  const [processedSize, setProcessedSize] = useState(0);
  const [compressionRatio, setCompressionRatio] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const fileInputRef = useRef();

  // Calculate compression ratio whenever processed size changes
  useEffect(() => {
    if (originalSize > 0 && processedSize > 0) {
      const ratio = ((originalSize - processedSize) / originalSize) * 100;
      setCompressionRatio(Math.round(ratio));
    }
  }, [processedSize, originalSize]);

  const handleImageUpload = async (file) => {
    if (!file) {
      toast.error(t('no_file_detected'));
      return false;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('supported_formats'));
      return false;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('file_size_limit'));
      return false;
    }

    const imageUrl = URL.createObjectURL(file);
    setOriginalImageUrl(imageUrl);
    setOriginalSize(file.size);

    const img = new Image();
    img.onload = () => {
      setOriginalDimensions({ 
        width: img.naturalWidth, 
        height: img.naturalHeight 
      });
      setWidth(img.naturalWidth);
      setHeight(img.naturalHeight);
    };
    img.src = imageUrl;
    setImageFile(file);
    
    setProcessedImageUrl("");
    setProcessedSize(0);
    setCompressionRatio(0);
    
    return false;
  };

  const simulateProgress = () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) {
          clearInterval(interval);
          return 95;
        }
        return prev + 10;
      });
    }, 100);
    return interval;
  };

  const processImage = async () => {
    if (!imageFile) {
      toast.error(t('upload_image_first'));
      return;
    }

    setIsProcessing(true);
    const progressInterval = simulateProgress();

    try {
      const formData = new FormData();
      formData.append("image", imageFile);

      if (mode === "resize") {
        formData.append("width", width);
        formData.append("height", height);
        formData.append("maintainRatio", maintainAspectRatio);
        formData.append("quality", quality);
        formData.append("format", targetFormat === "original" ? "auto" : targetFormat);
        
        const response = await api.post(`/image-resize`, formData, {
          responseType: "blob",
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 95) / progressEvent.total);
            setProgress(percent);
          },
        });

        const blob = response.data;
        if (blob) {
          const processedUrl = URL.createObjectURL(blob);
          setProcessedImageUrl(processedUrl);
          setProcessedSize(blob.size);
          setProgress(100);
          
          const img = new Image();
          img.onload = () => {
            if (img.width !== parseInt(width) || img.height !== parseInt(height)) {
              toast.info(t('actual_dimensions', { width: img.width, height: img.height }));
            }
          };
          img.src = processedUrl;
          
          toast.success(t('resize_success'));
        }
      } 
      else if (mode === "compress") {
        formData.append("width", width || originalDimensions.width);
        formData.append("height", height || originalDimensions.height);
        formData.append("maintainRatio", maintainAspectRatio);
        formData.append("quality", quality);
        formData.append("format", targetFormat === "original" ? "auto" : targetFormat);
        
        const response = await api.post(`/image-resize-compress`, formData, {
          responseType: "blob",
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 95) / progressEvent.total);
            setProgress(percent);
          },
        });

        const blob = response.data;
        if (blob) {
          const processedUrl = URL.createObjectURL(blob);
          setProcessedImageUrl(processedUrl);
          setProcessedSize(blob.size);
          setProgress(100);
          
          toast.success(t('compress_success'));
        }
      }
    } catch (err) {
      toast.error(mode === "resize" ? t('resize_failed') : t('compress_failed'));
    } finally {
      clearInterval(progressInterval);
      setIsProcessing(false);
    }
  };

  const downloadProcessedImage = () => {
    if (!processedImageUrl) return;
    
    const link = document.createElement("a");
    link.href = processedImageUrl;
    const extension = targetFormat === "original" 
      ? imageFile.name.split('.').pop() 
      : targetFormat;
    link.download = `processed-image.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetAll = () => {
    setImageFile(null);
    setOriginalImageUrl("");
    setOriginalDimensions({ width: 0, height: 0 });
    setOriginalSize(0);
    setProcessedImageUrl("");
    setProcessedSize(0);
    setCompressionRatio(0);
    setWidth("");
    setHeight("");
    setQuality(80);
    setProgress(0);
    
    if (originalImageUrl) URL.revokeObjectURL(originalImageUrl);
    if (processedImageUrl) URL.revokeObjectURL(processedImageUrl);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, white, #f0f9ff, white)',
    },
    mainContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    modeSelector: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '2rem',
      alignItems: 'center',
    },
    radioButton: {
      flex: 1,
      textAlign: 'center',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    card: {
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
    },
    uploadArea: {
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      padding: '2rem',
      cursor: 'pointer',
    },
    previewContainer: {
      background: '#f8fafc',
      borderRadius: '8px',
      padding: '1rem',
      border: '1px solid #e2e8f0',
    },
    previewImage: {
      width: '60px',
      height: '60px',
      borderRadius: '6px',
      objectFit: 'cover',
    },
    inputField: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
    },
    resultCard: {
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e5e7eb',
      height: '100%',
    },
    imageComparison: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    imageBox: {
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      background: '#f9fafb',
      padding: '0.5rem',
    },
    statsContainer: {
      background: '#f0f9ff',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '300px',
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.container}>
      <Header />
      
      <div style={styles.mainContent}>
        <div style={styles.modeSelector}>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            buttonStyle="solid"
            size="large"
            style={{ width: '100%', display: 'flex' }}
          >
            <Radio.Button value="resize" style={styles.radioButton}>
              {t('resize_mode')}
            </Radio.Button>
            <Radio.Button value="compress" style={styles.radioButton}>
              {t('compress_mode')}
            </Radio.Button>
          </Radio.Group>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Left Panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Card style={styles.card}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                  {t('upload_image')}
                </h3>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  {t('upload_limit')}
                </p>
              </div>

              {!imageFile ? (
                <Dragger
                  name="file"
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleImageUpload}
                  style={styles.uploadArea}
                >
                  <div style={{ padding: '2rem 0' }}>
                    <p style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      {t('drag_drop')}
                    </p>
                    <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                      {t('click_browse')}
                    </p>
                    <StandardButton type="secondary">
                      {t('select_image')}
                    </StandardButton>
                  </div>
                </Dragger>
              ) : (
                <div>
                  <div style={styles.previewContainer}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img 
                          src={originalImageUrl} 
                          alt="Preview" 
                          style={styles.previewImage}
                        />
                        <div>
                          <p style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
                            {imageFile.name}
                          </p>
                          <p style={{ fontSize: '13px', color: '#6b7280' }}>
                            {originalDimensions.width} × {originalDimensions.height}px
                          </p>
                          <p style={{ fontSize: '13px', color: '#6b7280' }}>
                            {formatFileSize(originalSize)}
                          </p>
                        </div>
                      </div>
                      <StandardButton 
                        type="secondary"
                        onClick={resetAll}
                        size="small"
                      >
                        {t('clear')}
                      </StandardButton>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {imageFile && (
              <Card style={styles.card}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                  {mode === "resize" ? t('resize_settings') : t('compress_settings')}
                </h3>
                
                {mode === "resize" ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                        {t('dimensions')}
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <input
                            type="number"
                            value={width}
                            onChange={(e) => {
                              const newWidth = parseInt(e.target.value) || "";
                              setWidth(newWidth);
                              if (maintainAspectRatio && originalDimensions.width && originalDimensions.height) {
                                const ratio = originalDimensions.height / originalDimensions.width;
                                setHeight(Math.round(newWidth * ratio));
                              }
                            }}
                            placeholder={t('width')}
                            style={styles.inputField}
                            min="1"
                            max="10000"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            value={height}
                            onChange={(e) => {
                              const newHeight = parseInt(e.target.value) || "";
                              setHeight(newHeight);
                              if (maintainAspectRatio && originalDimensions.width && originalDimensions.height) {
                                const ratio = originalDimensions.width / originalDimensions.height;
                                setWidth(Math.round(newHeight * ratio));
                              }
                            }}
                            placeholder={t('height')}
                            style={{ ...styles.inputField, opacity: maintainAspectRatio ? 0.6 : 1 }}
                            min="1"
                            max="10000"
                            disabled={maintainAspectRatio}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        {t('maintain_ratio')}
                      </label>
                      <Switch
                        checked={maintainAspectRatio}
                        onChange={setMaintainAspectRatio}
                        size="small"
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                          {t('quality')}
                        </label>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>{quality}%</span>
                      </div>
                      <Slider
                        value={quality}
                        onChange={setQuality}
                        min={1}
                        max={100}
                        tooltip={{ formatter: (value) => `${value}%` }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                        {t('output_format')}
                      </label>
                      <Radio.Group
                        value={targetFormat}
                        onChange={(e) => setTargetFormat(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Radio value="original" style={{ width: '100%', padding: '6px 0' }}>
                            {t('keep_original')}
                          </Radio>
                          <Radio value="jpeg" style={{ width: '100%', padding: '6px 0' }}>
                            JPEG
                          </Radio>
                          <Radio value="png" style={{ width: '100%', padding: '6px 0' }}>
                            PNG
                          </Radio>
                          <Radio value="webp" style={{ width: '100%', padding: '6px 0' }}>
                            WebP
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                          {t('quality')}
                        </label>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>{quality}%</span>
                      </div>
                      <Slider
                        value={quality}
                        onChange={setQuality}
                        min={1}
                        max={100}
                        tooltip={{ formatter: (value) => `${value}%` }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        <span>{t('smaller_file')}</span>
                        <span>{t('better_quality')}</span>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                        {t('output_format')}
                      </label>
                      <Radio.Group
                        value={targetFormat}
                        onChange={(e) => setTargetFormat(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Radio value="original" style={{ width: '100%', padding: '6px 0' }}>
                            {t('keep_original')}
                          </Radio>
                          <Radio value="jpeg" style={{ width: '100%', padding: '6px 0' }}>
                            JPEG
                          </Radio>
                          <Radio value="png" style={{ width: '100%', padding: '6px 0' }}>
                            PNG
                          </Radio>
                          <Radio value="webp" style={{ width: '100%', padding: '6px 0' }}>
                            WebP
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <StandardButton
                    type="secondary"
                    onClick={processImage}
                    loading={isProcessing}
                    style={{ width: '100%' }}
                    disabled={!imageFile}
                  >
                    {isProcessing ? t('processing') : (
                      mode === "resize" ? t('resize') : t('compress')
                    )}
                  </StandardButton>
                </div>

                {isProcessing && (
                  <div style={{ marginTop: '16px' }}>
                    <Progress
                      percent={progress}
                      status="active"
                      strokeColor={{
                        '0%': '#3b82f6',
                        '100%': '#10b981',
                      }}
                    />
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Right Panel */}
          <div>
            <Card style={styles.resultCard}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                {t('preview')}
              </h3>

              {processedImageUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={styles.imageComparison}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                        {t('original')}
                      </p>
                      <div style={styles.imageBox}>
                        <img
                          src={originalImageUrl}
                          alt="Original"
                          style={{ maxWidth: '100%', height: 'auto', margin: '0 auto', maxHeight: '200px', objectFit: 'contain' }}
                        />
                      </div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                        {originalDimensions.width} × {originalDimensions.height}px
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatFileSize(originalSize)}
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                        {t('processed')}
                      </p>
                      <div style={styles.imageBox}>
                        <img
                          src={processedImageUrl}
                          alt="Processed"
                          style={{ maxWidth: '100%', height: 'auto', margin: '0 auto', maxHeight: '200px', objectFit: 'contain' }}
                        />
                      </div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                        {width} × {height}px
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatFileSize(processedSize)}
                      </p>
                    </div>
                  </div>

                  <div style={styles.statsContainer}>
                    <div style={styles.statsGrid}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>
                          {compressionRatio}%
                        </div>
                        <div style={{ fontSize: '13px', color: '#4b5563' }}>
                          {t('size_reduction')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', marginBottom: '4px' }}>
                          {formatFileSize(originalSize - processedSize)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#4b5563' }}>
                          {t('space_saved')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <StandardButton
                      type="secondary"
                      onClick={downloadProcessedImage}
                      style={{ width: '100%' }}
                    >
                      {t('download')}
                    </StandardButton>
                  </div>

                  <StandardButton
                    type="secondary"
                    onClick={resetAll}
                    style={{ width: '100%' }}
                  >
                    {t('process_another')}
                  </StandardButton>
                </div>
              ) : (
                <div style={styles.emptyState}>
                  <h4 style={{ fontSize: '18px', color: '#9ca3af', marginBottom: '8px' }}>
                    {t('no_processed_image')}
                  </h4>
                  <p style={{ color: '#6b7280', maxWidth: '240px' }}>
                    {mode === "resize" 
                      ? t('resize_hint')
                      : t('compress_hint')}
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ImageResizer;